import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import {
  GetRegistrationSummaryQuery as GetRegistrationSummary,
} from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import EditRegistrationBackendMutation from './EditRegistrationBackendMutation';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import GetRegistrationQuery from './GetRegistrationQuery';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import RegistrationInfo from './RegistrationInfo';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

type Registration = GetRegistrationSummary['registrationSummary']['registrations'][0];

export interface UnassignRegistrationFormProps {
  registration: Registration;
  onSuccess: () => void;
  refetchQueries?: DocumentNode[];
}

export const UnassignRegistrationForm = ({
  registration, onSuccess, refetchQueries = [GetParticipantDataQuery, GetRegistrationSummaryQuery],
}: UnassignRegistrationFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      confirm: false,
    },
  });

  const [
    editRegistration,
    { error, loading },
  ] = useMutation(
    EditRegistrationBackendMutation,
    {
      variables: {
        input: {
          id: registration.id,
          unassign: true,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.registration_saved'));
        onSuccess();
      },
    },
  );

  const { valid } = useValidation({
    schema: yup.object({
      confirm: yup.boolean().required(),
    }),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editRegistration()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('unassign_ticket')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('unassign_ticket_description')}
          </UI.Paragraph>
        </UI.Div>

        <RegistrationInfo registration={registration} />

        <UI.ServerErrors error={error} />

        <UI.FormGrid>
          <UI.InputGroup>
            <UI.Checkbox {...form.checkbox('unassign')}>
              {t('unassign_ticket')}
            </UI.Checkbox>
          </UI.InputGroup>

        </UI.FormGrid>

        <UI.HR />

        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            type="submit"
            disabled={!form.changed() || !valid || loading}
            sc={{ brand: 'secondary', width: ['100%', 'auto'], loading }}
          >
            {t('confirm')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseUnassignRegistrationModalProps {
  registrationId: string;
  refetchQueries?: DocumentNode[];
}

const useUnassignRegistrationModal = ({
  registrationId, refetchQueries,
}: UseUnassignRegistrationModalProps) => {
  /**
   * Used to prevent state updates on an unmounted component.
   * When a registration is assigned away, the <RegistrationDetails /> component is unmounted.
   * If we don't check for this, React may throw an error.
   */
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  });

  const [open, setOpen] = useState(false);

  const { data } = useQuery(
    GetRegistrationQuery,
    {
      variables: {
        id: registrationId,
      },
      skip: !open,
    },
  );

  const registration = data?.registration;

  const [doOpenModal, closeModal] = useModal(() => {
    const handleSuccess = () => {
      if (mountedRef.current) {
        closeModal();
      }
    };

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        {!registration && <UI.Loader />}

        {registration && (
          <UnassignRegistrationForm
            registration={registration}
            refetchQueries={refetchQueries}
            onSuccess={handleSuccess}
          />
        )}
      </UI.Modal>
    );
  }, [registration, refetchQueries]);

  const openModal = () => {
    setOpen(true);
    doOpenModal();
  };

  return [openModal, closeModal];
};

export default useUnassignRegistrationModal;
